import { Box, Button, Refresh, Mui, MuiDataGridCommunity, Typography } from '@leap4/frontend';
import { useContext, useCallback, useMemo } from 'react';
import { ReportContext } from '../reportContext';
import { formatNumericDisplay, predictionErrorBand } from '../reports';
import type { IReportColumnDef } from '../report.interface';
import { numericFontFamily } from '../../../../theme/colors';

// The MIT grid, as the DSP uses: no license key, and every feature here is server-side anyway.
const { DataGrid, GridToolbarContainer, GridToolbarQuickFilter, GridToolbarColumnsButton, GridToolbarFilterButton, GridToolbarDensitySelector } = MuiDataGridCommunity;
type GridColDef = MuiDataGridCommunity.GridColDef;
const { Tooltip } = Mui;

/** The MIT grid's page size is capped at 100. */
const PAGE_SIZE_OPTIONS = [25, 50, 100];

// Ratios and de-duplicated counts: the server computes them over the whole report, so they are not row sums.
const OVERALL_METRIC = /frequency|(^|_)cpm$|(^|_)rpm$|^reach$/i;

const ReportGridToolbar = (props: { refresh?: () => void }) => (
    <GridToolbarContainer sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.75 }}>
        <GridToolbarQuickFilter sx={{ flex: 1, maxWidth: 300 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
            <GridToolbarColumnsButton />
            <GridToolbarFilterButton />
            <GridToolbarDensitySelector />
            {props.refresh && (
                <Button onClick={props.refresh} startIcon={<Refresh />} size="small">
                    Reload
                </Button>
            )}
        </Box>
    </GridToolbarContainer>
);

interface ReportTableProps {
    data: any[];
    columns: IReportColumnDef[];
    loading?: boolean;
    refreshFunc?: () => void;
}

// Columns without a format spec still carry numbers; group them for reading, and keep fractional credit visible.
const formatPlainNumber = (value: unknown): string => {
    const n = typeof value === 'number' ? value : Number(value);
    if (value === null || value === undefined || value === '' || !Number.isFinite(n)) return value == null ? '' : String(value);
    return n.toLocaleString('en-US', { maximumFractionDigits: Number.isInteger(n) ? 0 : 2 });
};

const formatCell = (col: IReportColumnDef, value: unknown): string => (col.format ? formatNumericDisplay(value, col.format) : formatPlainNumber(value));

/** A column is a metric when the server returned a total for it; dimensions never get one, and predictions have none. */
const isMetricColumn = (col: IReportColumnDef, totals: Record<string, any> | null, firstRow: Record<string, any> | undefined) =>
    !!col.format || !!col.isErrorPct || col.name.startsWith('predicted_') || (totals ? totals[col.name] !== undefined && totals[col.name] !== null : typeof firstRow?.[col.name] === 'number');

const convertToDataGridColumns = (columns: IReportColumnDef[], totals: Record<string, any> | null, firstRow: Record<string, any> | undefined): GridColDef[] => {
    return columns.map(col => {
        const colDef: GridColDef = {
            field: col.name,
            headerName: col.label || col.name,
            flex: 1,
            minWidth: 120,
            sortable: col.options?.sort !== false,
            filterable: col.options?.filter !== false,
        };

        if (isMetricColumn(col, totals, firstRow)) {
            colDef.valueFormatter = (value: unknown) => formatCell(col, value);
            colDef.align = 'right';
            colDef.headerAlign = 'right';
        }

        // Bands rather than a bare number: an error percentage is only ever read as "can I use this",
        // and the thresholds are a business call the reader should not have to remember.
        if (col.isErrorPct) {
            colDef.valueFormatter = (value: unknown) => {
                const pct = typeof value === 'number' ? value : Number.parseFloat(String(value ?? ''));
                return Number.isFinite(pct) ? `±${pct.toFixed(1)}%` : '';
            };
            colDef.cellClassName = (params: any) => {
                const band = predictionErrorBand(params.value);
                return band ? `pred-error-${band}` : '';
            };
            colDef.align = 'right';
            colDef.headerAlign = 'right';
        }

        return colDef;
    });
};

const TotalsStrip = ({ totals, columns }: { totals: Record<string, any>; columns: IReportColumnDef[] }) => {
    const items = columns.filter((c) => totals[c.name] !== undefined && totals[c.name] !== null);
    if (!items.length) return null;
    return (
        <Box component="dl" aria-label="Report totals" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, m: 0, mb: 1.5 }}>
            {items.map((c) => {
                const overall = OVERALL_METRIC.test(c.name);
                const value = formatCell(c, totals[c.name]);
                return (
                    <Box key={c.name} sx={{ border: 1, borderColor: 'divider', borderRadius: '8px', px: 1.5, py: 0.75, minWidth: 0 }}>
                        <Typography component="dt" sx={{ fontSize: '0.6875rem', color: 'text.secondary', display: 'flex', gap: 0.5, alignItems: 'center' }}>
                            {c.label || c.name}
                            {overall && (
                                <Tooltip title="Not a sum of the rows.">
                                    <Box component="span" tabIndex={0} sx={{ fontSize: '0.625rem', border: 1, borderColor: 'divider', borderRadius: '4px', px: 0.5 }}>
                                        overall
                                    </Box>
                                </Tooltip>
                            )}
                        </Typography>
                        <Typography component="dd" sx={{ m: 0, fontFamily: numericFontFamily, fontVariantNumeric: 'tabular-nums', fontWeight: 600, fontSize: '0.875rem' }}>
                            {value}
                        </Typography>
                    </Box>
                );
            })}
        </Box>
    );
};

const gridSx = {
    border: 1,
    borderColor: 'divider',
    borderRadius: '12px',
    overflow: 'hidden',
    bgcolor: 'background.paper',
    '& .MuiDataGrid-columnHeaders': { bgcolor: 'action.hover' },
    // Title, sort arrow and menu sit together, mirrored on right-aligned columns.
    '& .MuiDataGrid-columnHeaderTitleContainer': { flex: '0 1 auto' },
    '& .MuiDataGrid-columnHeaderTitle': { fontSize: '0.6875rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'text.secondary' },
    '& .MuiDataGrid-cell': { borderColor: 'divider', fontSize: '0.8125rem' },
    '& .MuiDataGrid-row:hover': { bgcolor: 'action.hover' },
    '& .MuiDataGrid-footerContainer': { borderColor: 'divider', bgcolor: 'action.hover' },
    '& .MuiDataGrid-columnSeparator': { opacity: 0, transition: 'opacity 0.15s', color: 'divider' },
    '& .MuiDataGrid-columnHeader:hover .MuiDataGrid-columnSeparator': { opacity: 1 },
    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.75rem', color: 'text.secondary' },
    '& .pred-error-good': { color: 'success.main', fontWeight: 600 },
    '& .pred-error-warn': { color: 'warning.main', fontWeight: 600 },
    '& .pred-error-bad': { color: 'error.main', fontWeight: 600 },
};

export const ReportTable = (props: ReportTableProps) => {
    const { data, columns, loading = false, refreshFunc } = props;
    const {
        totalCount,
        totals,
        paginationModel,
        onPaginationModelChange,
        sortModel,
        onSortModelChange,
        filterModel,
        onFilterModelChange,
    } = useContext(ReportContext);

    const gridColumns = useMemo(() => convertToDataGridColumns(columns, totals ?? null, data[0]), [columns, totals, data]);

    const rows = data.map((row, index) => ({
        id: `${row.pixel_date || ''}-${row.demand_id || ''}-${row.supply_id || ''}-${index}`,
        ...row,
    }));

    const handlePageChange = useCallback((newPage: number) => {
        onPaginationModelChange({ page: newPage, pageSize: paginationModel.pageSize });
    }, [onPaginationModelChange, paginationModel.pageSize]);

    const handlePageSizeChange = useCallback((newPageSize: number) => {
        onPaginationModelChange({ page: 0, pageSize: newPageSize });
    }, [onPaginationModelChange]);

    return (
        <Box sx={{ mt: 2 }}>
            {totals && rows.length > 0 && <TotalsStrip totals={totals} columns={columns} />}
            <Box sx={{ height: 640, width: '100%' }}>
                <DataGrid
                    key={gridColumns.map(col => col.field).join('|')}
                    rows={rows}
                    columns={gridColumns}
                    loading={loading}
                    sx={gridSx}
                    slots={{ toolbar: ReportGridToolbar as any }}
                    slotProps={{ toolbar: { refresh: refreshFunc } as any }}
                    showToolbar
                    paginationMode="server"
                    rowCount={totalCount}
                    // Fully controlled pagination, overriding the grid's internal model: it can never desync
                    // from the page actually fetched.
                    paginationModel={{ page: paginationModel.page, pageSize: Math.min(paginationModel.pageSize, 100) }}
                    onPaginationModelChange={(model: { page: number; pageSize: number }) => {
                        if (model.pageSize !== paginationModel.pageSize) {
                            handlePageSizeChange(model.pageSize);
                        } else if (model.page !== paginationModel.page) {
                            handlePageChange(model.page);
                        }
                    }}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    sortingMode="server"
                    sortModel={sortModel}
                    onSortModelChange={onSortModelChange}
                    filterMode="server"
                    filterModel={filterModel}
                    onFilterModelChange={onFilterModelChange}
                    disableRowSelectionOnClick
                />
            </Box>
        </Box>
    );
};
